// src/pages/admin/AdminStaffPage.js
import React, { useEffect, useState } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import pb from '../../lib/pocketbase';

export default function AdminStaffPage() {
    const [candidates, setCandidates] = useState([]);
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchUsers = async () => {
        try {
            // Fetch all users
            const allUsers = await pb.collection('users').getFullList({
                sort: '-created',
                requestKey: null
            });

            // Fetch generic application states to try and match
            let applicationStates = [];
            try {
                applicationStates = await pb.collection('onboarding_states').getFullList({
                    sort: '-created',
                    requestKey: null
                });
            } catch (e) {
                console.warn("Could not fetch onboarding_states (permission?)", e);
            }

            const candidatesList = [];
            const staffList = [];
            const otherUsersList = [];
            console.log(`Debug: Found ${allUsers.length} total users in DB query.`);

            allUsers.forEach(u => {
                // if (u.role === 'admin') return; // Commented out for debug to see self

                // Check if user has an application state
                // Relaxed match: check if step contains 'candidature'
                const appState = applicationStates.find(s => s.user === u.id && (s.step || '').includes('candidature'));

                if (appState && !['commercial', 'developpeur'].includes(u.role)) {
                    candidatesList.push({ ...u, application: appState });
                } else if (['commercial', 'developpeur'].includes(u.role)) {
                    staffList.push(u);
                } else if (u.role === 'user') {
                    // Fallback: This user is role 'user' but we didn't find a linked app state.
                    // We list them anyway so admin can manually promote them.
                    otherUsersList.push(u);
                }
            });

            console.log(`Found: ${candidatesList.length} candidates, ${staffList.length} staff, ${otherUsersList.length} others`);
            setCandidates([...candidatesList, ...otherUsersList.map(u => ({ ...u, isFallback: true }))]);
            setStaff(staffList);
            setLoading(false);
        } catch (err) {
            console.error("Error loading staff", err);
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleApprove = async (candidate) => {
        if (!window.confirm(`Valider la candidature de ${candidate.name} pour le poste de ${candidate.application.step.split('_')[1]} ?`)) return;

        try {
            const targetRole = candidate.application.step.split('_')[1]; // 'commercial' or 'developpeur'

            // Update user role
            await pb.collection('users').update(candidate.id, {
                role: targetRole,
                // verified: true // System field, might be protected against non-superuser updates
            });

            // Update application state
            await pb.collection('onboarding_states').update(candidate.application.id, {
                status: 'completed',
                completed: true
            });

            // Refresh
            fetchUsers();
        } catch (err) {
            console.error("Error approving candidate", err);
            alert("Erreur validation : " + err.message);
        }
    };

    const handleReject = async (candidate) => {
        if (!window.confirm(`Rejeter la candidature de ${candidate.name} ? (Cela supprimera le compte)`)) return;
        try {
            await pb.collection('users').delete(candidate.id);
            fetchUsers();
        } catch (err) {
            alert("Erreur lors du rejet");
        }
    };

    const handlePromoteManual = async (candidate, targetRole) => {
        if (!window.confirm(`Promouvoir ${candidate.name} en tant que ${targetRole} ?`)) return;
        try {
            await pb.collection('users').update(candidate.id, {
                role: targetRole,
                verified: true
            });
            fetchUsers();
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la promotion");
        }
    }


    const handleDemote = async (member) => {
        if (!window.confirm(`⚠️ ATTENTION : Êtes-vous sûr de vouloir licencier ${member.name} ?\n\nCela lui retirera immédiatement son rôle de "${member.role}" et son accès à l'espace dédié.\nIl redeviendra un simple utilisateur.`)) return;

        try {
            await pb.collection('users').update(member.id, {
                role: 'user'
            });
            fetchUsers();
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la révocation des droits : " + err.message);
        }
    };

    const handlePasswordReset = async (user) => {
        const newPassword = prompt(`🔑 Entrez le nouveau mot de passe pour ${user.name || user.email} :\n(Vous devrez le transmettre manuellement à l'utilisateur)`);
        if (!newPassword) return;

        if (newPassword.length < 5) { // Pocketbase default is often 5 or 8, safely say 5 here or just try
            alert("Le mot de passe est trop court.");
            return;
        }

        try {
            await pb.collection('users').update(user.id, {
                password: newPassword,
                passwordConfirm: newPassword,
            });
            alert(`✅ Mot de passe modifié avec succès pour ${user.name || user.email}.\n\nNouveau mot de passe : ${newPassword}\n\n👉 Transmettez ce mot de passe à l'utilisateur, il pourra se connecter immédiatement.`);
        } catch (err) {
            console.error(err);
            alert("Erreur lors de la modification du mot de passe : " + err.message);
        }
    };

    const roleEmoji = (r) => {
        if (r === 'commercial') return '💼';
        if (r === 'developpeur') return '👨‍💻';
        return '👤';
    }

    const styles = {
        grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px' },
        card: {
            background: 'var(--admin-panel)',
            border: '1px solid var(--admin-border)',
            borderRadius: '16px',
            padding: '20px',
            boxShadow: '0 4px 6px rgba(0,0,0,0.02)',
            transition: 'transform 0.2s, box-shadow 0.2s'
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse',
            fontSize: '0.95rem'
        },
        th: {
            textAlign: 'left',
            padding: '16px',
            color: 'var(--admin-muted)',
            borderBottom: '1px solid var(--admin-border)',
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em'
        },
        td: {
            padding: '16px',
            borderBottom: '1px solid var(--admin-border)',
            verticalAlign: 'middle',
            color: 'var(--admin-text)'
        },
        badge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 10px',
            borderRadius: '99px',
            fontSize: '0.8rem',
            fontWeight: 500
        }
    };

    return (
        <AdminLayout title="Gestion du Staff">
            <div className="admin-page-header" style={{ marginBottom: '32px' }}>
                <h1 style={{
                    fontSize: '2rem',
                    fontWeight: '800',
                    background: 'linear-gradient(135deg, var(--admin-text) 0%, var(--admin-muted) 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: '8px'
                }}>
                    Équipe & Recrutement
                </h1>
                <p style={{ color: 'var(--admin-muted)', fontSize: '1.1rem' }}>
                    Gérez vos collaborateurs et validez les nouvelles candidatures.
                </p>
            </div>

            {loading ? (
                <div style={{ padding: '40px', textAlign: 'center', color: 'var(--admin-muted)' }}>Chargement des données...</div>
            ) : (
                <div style={{ display: 'grid', gap: '40px' }}>

                    {/* SECTION CANDIDATURES */}
                    <section>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Candidatures en attente</h2>
                            {candidates.length > 0 &&
                                <span style={{ background: 'var(--admin-accent)', color: 'white', padding: '2px 10px', borderRadius: '12px', fontSize: '0.85rem', fontWeight: 700 }}>
                                    {candidates.length}
                                </span>
                            }
                        </div>

                        {candidates.length === 0 ? (
                            <div style={{
                                padding: '40px',
                                border: '2px dashed var(--admin-border)',
                                borderRadius: '16px',
                                textAlign: 'center',
                                color: 'var(--admin-muted)'
                            }}>
                                <div style={{ fontSize: '2rem', marginBottom: '10px', opacity: 0.5 }}>📭</div>
                                <div>Aucune nouvelle candidature pour le moment.</div>
                            </div>
                        ) : (
                            <div style={styles.grid}>
                                {candidates.map(c => {
                                    const isFallback = c.isFallback;
                                    const requestedRole = isFallback ? 'Incconu' : c.application.step.split('_')[1];

                                    return (
                                        <div key={c.id} style={styles.card} className="hover-lift">
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' }}>
                                                <div style={{ display: 'flex', gap: '12px' }}>
                                                    <div style={{
                                                        width: '48px', height: '48px',
                                                        borderRadius: '12px',
                                                        background: 'linear-gradient(135deg, var(--admin-panel-2), var(--admin-border))',
                                                        display: 'grid', placeItems: 'center',
                                                        fontSize: '1.5rem',
                                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                    }}>
                                                        {c.avatar ? <img src={pb.files.getUrl(c, c.avatar)} alt="" style={{ width: '100%', height: '100%', borderRadius: '12px' }} /> : '👤'}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: '700', fontSize: '1.05rem' }}>{c.name}</div>
                                                        <div style={{ color: 'var(--admin-muted)', fontSize: '0.9rem' }}>{c.email}</div>
                                                    </div>
                                                </div>
                                                <div style={{
                                                    background: 'var(--admin-panel-2)',
                                                    padding: '4px 8px', borderRadius: '6px',
                                                    fontSize: '0.75rem', color: 'var(--admin-muted)',
                                                    border: '1px solid var(--admin-border)'
                                                }}>
                                                    {new Date(c.created).toLocaleDateString()}
                                                </div>
                                            </div>

                                            <div style={{
                                                background: 'rgba(79, 70, 229, 0.1)',
                                                color: 'var(--admin-accent)',
                                                padding: '12px', borderRadius: '10px',
                                                marginBottom: '20px', fontSize: '0.9rem',
                                                display: 'flex', alignItems: 'center', gap: '8px'
                                            }}>
                                                <span>Souhaite rejoindre en tant que :</span>
                                                <strong style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                    {roleEmoji(requestedRole)}
                                                    {requestedRole?.charAt(0).toUpperCase() + requestedRole?.slice(1)}
                                                </strong>
                                            </div>

                                            <div style={{ display: 'flex', gap: '10px' }}>
                                                {isFallback ? (
                                                    <>
                                                        <button className="btn btn-sm btn-info" style={{ flex: 1 }} onClick={() => handlePromoteManual(c, 'commercial')}>Forcer Commercial</button>
                                                        <button className="btn btn-sm btn-info" style={{ flex: 1 }} onClick={() => handlePromoteManual(c, 'developpeur')}>Dev</button>
                                                        <button
                                                            className="btn btn-sm"
                                                            style={{ background: 'var(--admin-panel-2)', border: '1px solid var(--admin-border)', color: 'var(--admin-text)' }}
                                                            onClick={() => handlePasswordReset(c)}
                                                            title="Réinitialiser le mot de passe"
                                                        >
                                                            🔑 Recréer MDP
                                                        </button>
                                                    </>
                                                ) : (
                                                    <button
                                                        className="btn"
                                                        style={{
                                                            flex: 1,
                                                            background: 'var(--admin-accent)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '8px',
                                                            padding: '8px',
                                                            fontWeight: 600,
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() => handleApprove(c)}
                                                    >
                                                        Valider la candidature
                                                    </button>
                                                )}
                                                <button
                                                    className="btn"
                                                    style={{
                                                        background: 'rgba(239, 68, 68, 0.1)',
                                                        color: '#ef4444',
                                                        border: 'none',
                                                        borderRadius: '8px',
                                                        padding: '0 12px',
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => handleReject(c)}
                                                    title="Rejeter"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </section>

                    {/* SECTION STAFF */}
                    <section>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, margin: 0 }}>Membres de l'équipe</h2>
                            <span style={{ background: 'var(--admin-panel-2)', color: 'var(--admin-muted)', padding: '2px 10px', borderRadius: '12px', fontSize: '0.85rem', border: '1px solid var(--admin-border)' }}>
                                {staff.length} actifs
                            </span>
                        </div>

                        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={styles.table}>
                                    <thead>
                                        <tr style={{ background: 'var(--admin-panel-2)' }}>
                                            <th style={styles.th}>Collaborateur</th>
                                            <th style={styles.th}>Contact</th>
                                            <th style={styles.th}>Rôle</th>
                                            <th style={styles.th}>Arrivée</th>
                                            <th style={styles.th}>Statut</th>
                                            <th style={styles.th}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {staff.map((s, idx) => (
                                            <tr key={s.id} style={{
                                                background: idx % 2 === 0 ? 'transparent' : 'rgba(0,0,0,0.01)',
                                                transition: 'background 0.2s'
                                            }}>
                                                <td style={styles.td}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                        <div style={{
                                                            width: '36px', height: '36px', borderRadius: '50%',
                                                            background: 'linear-gradient(135deg, #e0e7ff, #c7d2fe)',
                                                            color: '#4338ca',
                                                            display: 'grid', placeItems: 'center', fontSize: '1rem', fontWeight: 700
                                                        }}>
                                                            {s.avatar ? (
                                                                <img src={pb.files.getUrl(s, s.avatar)} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                                                            ) : (
                                                                s.name.charAt(0).toUpperCase()
                                                            )}
                                                        </div>
                                                        <span style={{ fontWeight: 600 }}>{s.name}</span>
                                                    </div>
                                                </td>
                                                <td style={styles.td}>{s.email}</td>
                                                <td style={styles.td}>
                                                    <span style={{
                                                        ...styles.badge,
                                                        background: s.role === 'commercial' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(168, 85, 247, 0.1)',
                                                        color: s.role === 'commercial' ? '#2563eb' : '#9333ea',
                                                        border: `1px solid ${s.role === 'commercial' ? 'rgba(59, 130, 246, 0.2)' : 'rgba(168, 85, 247, 0.2)'}`
                                                    }}>
                                                        {roleEmoji(s.role)} {s.role.charAt(0).toUpperCase() + s.role.slice(1)}
                                                    </span>
                                                </td>
                                                <td style={{ ...styles.td, color: 'var(--admin-muted)', fontSize: '0.9rem' }}>
                                                    {new Date(s.created).toLocaleDateString()}
                                                </td>
                                                <td style={styles.td}>
                                                    {s.verified ? (
                                                        <span style={{ color: '#10b981', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem' }}>
                                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#10b981' }}></span> Vérifié
                                                        </span>
                                                    ) : (
                                                        <span style={{ color: 'var(--admin-muted)', display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.85rem' }}>
                                                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--admin-border)' }}></span> En attente
                                                        </span>
                                                    )}
                                                </td>
                                                <td style={styles.td}>
                                                    <button
                                                        onClick={() => handleDemote(s)}
                                                        style={{
                                                            background: 'rgba(239, 68, 68, 0.1)',
                                                            color: '#ef4444',
                                                            border: '1px solid rgba(239, 68, 68, 0.2)',
                                                            padding: '6px 12px',
                                                            borderRadius: '6px',
                                                            fontSize: '0.8rem',
                                                            cursor: 'pointer',
                                                            fontWeight: 600,
                                                            display: 'flex', alignItems: 'center', gap: '6px'
                                                        }}
                                                        title="Révoquer les accès et remettre en rôle 'user'"
                                                    >
                                                        <span>🚨</span> Licencier
                                                    </button>
                                                    <button
                                                        onClick={() => handlePasswordReset(s)}
                                                        style={{
                                                            background: 'rgba(245, 158, 11, 0.1)',
                                                            color: '#d97706',
                                                            border: '1px solid rgba(245, 158, 11, 0.2)',
                                                            padding: '6px 8px',
                                                            borderRadius: '6px',
                                                            fontSize: '0.8rem',
                                                            cursor: 'pointer',
                                                            fontWeight: 600,
                                                            display: 'flex', alignItems: 'center', gap: '6px'
                                                        }}
                                                        title="Définir un nouveau mot de passe"
                                                    >
                                                        🔑 MDP
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>

                </div>
            )}
        </AdminLayout>
    );
}
